import { useMutation } from "@tanstack/react-query";
import type { SessionMutationMethodName, SessionMethodParams, SessionMethodReturn } from "../types";
import type { UseTellescopeSessionMutationOptions } from "./types";
import { useMemo } from "react";
import { buildSessionQueryKey } from "../keys";
import { addKeyToMutationResult, executeTellescopeSessionMethod } from "../utils";
import { useTellescopeSession } from "../context";

/**
 * A hook for performing Tellescope session mutations.
 * Session mutations are operations that modify session state, such as authentication, logout, etc.
 *
 * @template Method - The session mutation method name (e.g., "authenticate", "logout")
 * @template TError - The error type that can be thrown (defaults to Error)
 * @template TOnMutateResult - The external context type for custom mutation logic (defaults to undefined)
 *
 * @param options - Configuration object with the following properties:
 * @param options.method - The session mutation method to call (e.g., "authenticate", "logout")
 * @param options.session - Optional session for authentication (uses context session if not provided)
 *
 * @returns A mutation object with mutate/mutateAsync functions and mutation state
 *
 * @example
 * ```tsx
 * // Authenticate user
 * const loginMutation = useTellescopeSessionMutation({
 *   method: 'authenticate',
 *   onSuccess: (data) => {
 *     console.log('Logged in:', data);
 *   },
 * });
 *
 * loginMutation.mutate({
 *   email: 'user@example.com',
 *   password: 'password123',
 *   o: { mfa_token: '123456' },
 * });
 * ```
 *
 * @example
 * ```tsx
 * // Logout user
 * const logoutMutation = useTellescopeSessionMutation({
 *   method: 'logout',
 *   onSuccess: () => {
 *     console.log('Logged out successfully');
 *   },
 * });
 *
 * logoutMutation.mutate(undefined);
 * ```
 *
 * @example
 * ```tsx
 * // Change tenant
 * const changeTenantMutation = useTellescopeSessionMutation({
 *   method: 'change_tenant',
 *   onSuccess: (data) => {
 *     console.log('Changed tenant:', data);
 *   },
 * });
 *
 * changeTenantMutation.mutate({
 *   businessId: 'business123',
 * });
 * ```
 */
export const useTellescopeSessionMutation = <
  Method extends SessionMutationMethodName,
  TError = Error,
  TOnMutateResult = undefined,
>({
  method,
  session: providedSession,
  ...tanstackOptions
}: UseTellescopeSessionMutationOptions<Method, TError, TOnMutateResult>) => {
  const session = useTellescopeSession(providedSession);
  const mutationKey = useMemo(() => buildSessionQueryKey({ method }), [method]);

  const mutation = useMutation<SessionMethodReturn<Method>, TError, SessionMethodParams<Method>, TOnMutateResult>({
    mutationKey,
    mutationFn: (request) => {
      if (session === null) {
        throw new Error(
          "No Tellescope session provided for mutation. Mutations should not be executed if session is `null`"
        );
      }
      return executeTellescopeSessionMethod({
        session,
        method,
        request,
        parseISOStrings: true, // TODO: improve this by making it an option locally and globally
      });
    },
    ...tanstackOptions,
  });

  return addKeyToMutationResult(mutation, mutationKey);
};
